import type { AuthPayload } from '@notify/shared';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { api } from '../services/api';
import type { AuthTokens } from '../store/auth.store';
import { useAuthStore } from '../store/auth.store';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'shop' | 'admin';

interface LoginResponse {
  user: AuthPayload;
  tokens: AuthTokens;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function BellIcon() {
  return (
    <svg
      className="h-7 w-7 text-[#6366F1]"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 22a2 2 0 001.995-1.85L14 20H10a2 2 0 001.995 1.85L12 22zM12 2a7 7 0 016.996 6.76L19 9v5l2 3v1H3v-1l2-3V9a7 7 0 017-7z" />
    </svg>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg
      className="h-4 w-4 text-[#64748B]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  ) : (
    <svg
      className="h-4 w-4 text-[#64748B]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      className="h-4 w-4 flex-shrink-0 text-[#EF4444]"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ─── Login form ───────────────────────────────────────────────────────────────

interface LoginFormProps {
  tab: Tab;
}

function LoginForm({ tab }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();

  function validate(): boolean {
    let valid = true;
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError('Email is required');
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Enter a valid email address');
      valid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      valid = false;
    }

    return valid;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!validate()) return;

    setIsLoading(true);
    try {
      const endpoint =
        tab === 'admin' ? '/api/v1/auth/admin/login' : '/api/v1/auth/shop/login';

      const result = await api.post<LoginResponse>(endpoint, { email, password });

      login(result.user, result.tokens);

      if (result.user.role === 'admin') {
        void navigate('/admin/dashboard');
      } else {
        void navigate('/dashboard');
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5" noValidate>
      {/* Email */}
      <div>
        <label htmlFor="email" className="label">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          className={`input mt-1 ${emailError ? 'border-[#EF4444] focus:border-[#EF4444] focus:ring-[#EF4444]' : ''}`}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError('');
          }}
        />
        {emailError && (
          <p className="mt-1 flex items-center gap-1 text-xs text-[#EF4444]">
            <ErrorIcon />
            {emailError}
          </p>
        )}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="password" className="label">
          Password
        </label>
        <div className="relative mt-1">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            className={`input pr-10 ${passwordError ? 'border-[#EF4444] focus:border-[#EF4444] focus:ring-[#EF4444]' : ''}`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError('');
            }}
          />
          <button
            type="button"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute inset-y-0 right-0 flex items-center pr-3 focus:outline-none"
            onClick={() => setShowPassword((v) => !v)}
          >
            <EyeIcon open={showPassword} />
          </button>
        </div>
        {passwordError && (
          <p className="mt-1 flex items-center gap-1 text-xs text-[#EF4444]">
            <ErrorIcon />
            {passwordError}
          </p>
        )}
      </div>

      {/* Form-level error */}
      {formError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <ErrorIcon />
          <p className="text-sm text-[#EF4444]">{formError}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full gap-2"
      >
        {isLoading && <SpinnerIcon />}
        {isLoading ? 'Signing in…' : 'Sign in'}
      </button>

      {/* Forgot password */}
      <p className="text-center text-sm text-[#64748B]">
        <a
          href="/forgot-password"
          className="font-medium text-[#6366F1] hover:text-indigo-700 focus:outline-none focus-visible:underline"
        >
          Forgot password?
        </a>
      </p>
    </form>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const [activeTab, setActiveTab] = useState<Tab>('shop');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  // Redirect away if already authenticated
  if (isAuthenticated && user) {
    return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-4 py-12">
      <div className="card w-full max-w-md">
        {/* Brand */}
        <div className="mb-7 flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <BellIcon />
            <span className="text-2xl font-bold tracking-tight text-[#0F172A]">Notify</span>
          </div>
          <p className="text-sm text-[#64748B]">Sign in to your account</p>
        </div>

        {/* Tab switcher */}
        <div className="mb-6 flex rounded-lg bg-[#F1F5F9] p-1">
          <button
            type="button"
            onClick={() => setActiveTab('shop')}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6366F1] focus-visible:ring-offset-1 ${
              activeTab === 'shop'
                ? 'bg-white text-[#0F172A] shadow-sm'
                : 'text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            Shop Login
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('admin')}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6366F1] focus-visible:ring-offset-1 ${
              activeTab === 'admin'
                ? 'bg-white text-[#0F172A] shadow-sm'
                : 'text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            Admin Login
          </button>
        </div>

        {/* Form — key forces full remount (and state reset) on tab change */}
        <LoginForm key={activeTab} tab={activeTab} />
      </div>
    </div>
  );
}
